import {
  PlusOutlined,
  EditOutlined,
  MessageOutlined,
  UserSwitchOutlined,
  SendOutlined,
  RedoOutlined,
  CheckOutlined,
  CloseOutlined,
  UndoOutlined,
  DeleteOutlined,
  SwapOutlined,
  PaperClipOutlined,
  ClockCircleOutlined,
  HistoryOutlined,
} from '@ant-design/icons';

/*
 * Maps an activity/history row to a small, meaningful icon + accent color
 * instead of one repeated dot for every event. Keyed by the backend's loose
 * `activityType` (case-insensitive, values are inconsistent across services —
 * "add" | "Assignment" | "Submitted" | "reject_days" | ... — so this is an
 * alias table, not an enum) with a keyword fallback over the comment text for
 * older rows that never got a type.
 *
 * Shared by the generic Activity tab (jobs/candidates/submissions/onboarding/
 * assignment/client) and Timesheet's review history, so "approved" looks the
 * same shade of green everywhere in the app.
 */
const TYPE_ALIASES = {
  add: 'created',
  create: 'created',
  created: 'created',

  update: 'updated',
  updated: 'updated',
  edit: 'updated',
  edited: 'updated',
  // Timesheet-specific: a day's hours/logs edited, or the proof screenshot
  // replaced, during the rejected/reopened correction window before a
  // resubmit — see ReviewHistoryCard in the ZINNEXT-V2 Timesheet pages.
  update_log: 'updated',
  update_proof: 'updated',

  notes: 'note',
  note: 'note',
  comment: 'note',

  assign: 'assigned',
  assigned: 'assigned',
  assignment: 'assigned',
  reassign: 'assigned',
  reassigned: 'assigned',

  submit: 'submitted',
  submitted: 'submitted',
  submission: 'submitted',
  'internal submission': 'submitted',
  send_for_approval: 'sendForApproval',
  'send for approval': 'sendForApproval',
  sendforapproval: 'sendForApproval',

  resubmit: 'resubmitted',
  resubmitted: 'resubmitted',
  // A manager's Re-Approve "Send For Approval" (rate corrected after a CFO
  // rejection, resent to the CFO) — distinct from a fresh "approved" so the
  // timeline doesn't show two approvals for one request.
  resent_for_approval: 'resent',

  reject: 'rejected',
  rejected: 'rejected',
  reject_days: 'rejected',
  declined: 'rejected',
  decline: 'rejected',

  approve: 'approved',
  approved: 'approved',
  approve_days: 'approved',

  reopen: 'reopened',
  reopened: 'reopened',

  delete: 'deleted',
  deleted: 'deleted',
  remove: 'deleted',
  removed: 'deleted',

  status: 'status',
  'status change': 'status',
  statuschange: 'status',

  document: 'document',
  documents: 'document',
  upload: 'document',
  uploaded: 'document',
  attachment: 'document',
};

const ACTION_META = {
  created:      { tone: 'success', icon: <PlusOutlined />,       label: 'Created' },
  updated:      { tone: 'info',    icon: <EditOutlined />,       label: 'Updated' },
  note:         { tone: 'neutral', icon: <MessageOutlined />,    label: 'Note' },
  assigned:     { tone: 'purple',  icon: <UserSwitchOutlined />, label: 'Assigned' },
  submitted:    { tone: 'info',    icon: <SendOutlined />,       label: 'Submitted' },
  sendForApproval: { tone: 'info', icon: <SendOutlined />,       label: 'Send for Approval' },
  resubmitted:  { tone: 'purple',  icon: <RedoOutlined />,       label: 'Re-submitted' },
  resent:       { tone: 'purple',  icon: <RedoOutlined />,       label: 'Resend for Approval' },
  approved:     { tone: 'success', icon: <CheckOutlined />,      label: 'Approved' },
  rejected:     { tone: 'danger',  icon: <CloseOutlined />,      label: 'Rejected' },
  reopened:     { tone: 'neutral', icon: <UndoOutlined />,       label: 'Reopened' },
  deleted:      { tone: 'danger',  icon: <DeleteOutlined />,     label: 'Deleted' },
  status:       { tone: 'warning', icon: <SwapOutlined />,       label: 'Status changed' },
  document:     { tone: 'info',    icon: <PaperClipOutlined />,  label: 'Document' },
  scheduled:    { tone: 'warning', icon: <ClockCircleOutlined />, label: 'Scheduled' },
  default:      { tone: 'neutral', icon: <HistoryOutlined />,    label: 'Activity' },
};

// Fallback keyword sniff for rows with no (or an unrecognized) activityType —
// mostly older free-text activityComments like "Job created and assigned...".
const KEYWORD_ACTIONS = [
  // Must be checked before the generic `approv` rule. These descriptions
  // contain the word "approval", but represent a request sent to the CFO,
  // not an approval completed by the CFO.
  [/\b(?:submitted|sent|send)\b[\s\S]*\b(?:cfo|finance)\b[\s\S]*\bfor approval\b/i, 'sendForApproval'],
  [/\bsend(?:ing)?\s+for\s+approval\b/i, 'sendForApproval'],
  [/\breject/i, 'rejected'],
  // Checked BEFORE the broad \bapprov match: a resend sentence naturally
  // contains "...for CFO approval" (matches \bapprov too), so resend/resubmit
  // must win first or every resend keyword-falls-back to "Approved".
  [/\bresent\b/i, 'resent'],
  [/\bresubmit/i, 'resubmitted'],
  [/\bapprov/i, 'approved'],
  [/\bsubmit/i, 'submitted'],
  [/\breassign|\bassign/i, 'assigned'],
  [/\bdelete|\bremov/i, 'deleted'],
  [/\breopen/i, 'reopened'],
  [/\bstatus/i, 'status'],
  [/\bdocument|\battach|\bupload/i, 'document'],
  [/\bnote/i, 'note'],
  [/\bupdat|\bchang|\bedit/i, 'updated'],
  [/\bcreat|\badded\b/i, 'created'],
];

function resolveAction(activityType, text) {
  const typeKey = String(activityType || '').trim().toLowerCase();
  if (typeKey && TYPE_ALIASES[typeKey]) return TYPE_ALIASES[typeKey];

  const plain = String(text || '');
  for (const [pattern, action] of KEYWORD_ACTIONS) {
    if (pattern.test(plain)) return action;
  }
  return 'default';
}

/* row: activity/history entry — reads activityType/action + a text fallback. */
export function resolveActivityMeta(row = {}, plainText = '') {
  const activityType =
    row.activityType ??
    row.activity_type ??
    row.type ??
    row.action;
  const action = resolveAction(activityType, plainText || row.activityComments);
  return ACTION_META[action] ?? ACTION_META.default;
}

